import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { enqueueSnackbar } from "notistack";

import { queryKeys } from "@/shared/utils/queryKeys";
import { useDemoMode } from "@/pages/demo/DemoModeContext";
import { getAccessToken } from "@/shared/types/auth";
import {
  EstateItemsResponse,
  CreateEstateItemRequest,
  EstateItem,
} from "@/shared/types/estateTypes";
import { trackEvent, EventType } from "@/shared/utils/core";
import { sentryUtils } from "@/shared/utils/sentry";
import { buildAddressFromMetadata } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { canonicalItemAddressKey } from "@/shared/hooks/useCanonicalAddress";

type MutationOp = "add" | "update" | "delete" | "approve" | "decline";

function reportMutationFailure(
  err: unknown,
  operation: MutationOp,
  estateIdForContext: number | null | undefined,
) {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : String(err);
  const isAuthOrClient = msg.includes("Authentication required") || /\b(401|403)\b/.test(msg);
  sentryUtils.captureError(
    err,
    "Estate items mutation failure",
    {
      estateId: estateIdForContext ?? undefined,
      financials_operation: operation,
    },
    isAuthOrClient ? "warning" : "error",
  );
}

function asEstateItem(data: unknown): EstateItem | null {
  if (data && typeof data === "object" && "id" in data && "category" in data) {
    return data as EstateItem;
  }
  return null;
}

function maintainPropertySitus(item: EstateItem, queryClient: QueryClient): void {
  if (item.category !== "Properties") return;
  void resolveAddressLink({
    raw: buildAddressFromMetadata(item.metadata),
    purpose: "situs",
    entity: { estate_item_id: item.id },
  }).then((result) => {
    void queryClient.invalidateQueries({ queryKey: canonicalItemAddressKey(item.id) });
    if (result?.duplicateSitus) {
      enqueueSnackbar("This property address is already listed in this estate's inventory.", {
        variant: "warning",
      });
    }
  });
}

async function fetchEstateItems(estateId: number): Promise<EstateItemsResponse> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${estateId}/items`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Estate items fetch error:", response.status, errorText);
    throw new Error(`Failed to fetch estate items: ${response.status} - ${errorText}`);
  }

  return response.json();
}

async function createEstateItem(item: CreateEstateItemRequest) {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${item.estate_id}/items`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(item),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to add item: ${response.status} - ${errorText}`);
  }

  return response.json();
}

async function updateEstateItem({
  id,
  estateId,
  updates,
}: {
  id: number;
  estateId: number;
  updates: Partial<CreateEstateItemRequest>;
}) {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${estateId}/items/${id}`;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update item: ${response.status} - ${errorText}`);
  }

  return response.json();
}

async function deleteEstateItem({ id, estateId }: { id: number; estateId: number }) {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${estateId}/items/${id}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete item: ${response.status} - ${errorText}`);
  }

  return response.json();
}

async function approveEstateItem({ id, estateId }: { id: number; estateId: number }) {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${estateId}/items/${id}/approve`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to approve item: ${response.status} - ${errorText}`);
  }

  return response.json();
}

async function declineEstateItem({ id, estateId }: { id: number; estateId: number }) {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("Authentication required");
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/financials/${estateId}/items/${id}/decline`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to decline item: ${response.status} - ${errorText}`);
  }

  return response.json();
}

export function useEstateItems(estateId: number | null | undefined) {
  const queryClient = useQueryClient();
  const { isDemoMode } = useDemoMode();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: queryKeys.estate.items(estateId!),
    queryFn: () => fetchEstateItems(estateId!),
    enabled: !!estateId && !isDemoMode,
    staleTime: isDemoMode ? Infinity : 0,
    refetchOnWindowFocus: isDemoMode ? false : true,
    refetchOnMount: isDemoMode ? false : "always",
    retry: 1,
    refetchInterval: isDemoMode ? false : 10000,
  });

  const addItemMutation = useMutation({
    mutationFn: async (variables: CreateEstateItemRequest) => {
      if (isDemoMode) {
        const now = new Date().toISOString();
        const fakeItem: EstateItem = {
          id: -Date.now(),
          estate_id: variables.estate_id,
          category: variables.category,
          item_name: variables.item_name,
          item_type: variables.item_type,
          balance: variables.balance,
          classification: variables.classification,
          last_updated_date: variables.last_updated_date,
          ownership: variables.ownership ?? null,
          notes: variables.notes,
          created_at: now,
          updated_at: now,
          created_by_user_id: 0,
          metadata: variables.metadata,
          is_estimated: variables.is_estimated,
          balance_estimate_range: variables.balance_estimate_range ?? null,
          value_label: variables.value_label ?? null,
          status_flag: variables.status_flag ?? null,
          linked_item_id: variables.linked_item_id ?? null,
          source_document_id: null,
          extracted_from_document: false,
        };
        const prev = queryClient.getQueryData<EstateItemsResponse>(
          queryKeys.estate.items(estateId!),
        );
        if (prev) {
          const list = variables.classification === "asset" ? "assets" : "debts";
          queryClient.setQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!), {
            ...prev,
            [list]: [fakeItem, ...prev[list]],
          });
        }
        return;
      }
      return createEstateItem(variables);
    },
    onSuccess: (data, variables) => {
      if (isDemoMode) return;
      trackEvent(EventType.FINANCIAL_ITEM_ADDED, {
        classification: variables.classification,
        category: variables.category,
        item_type: variables.item_type,
        has_value: !!variables.balance,
      });

      const created = asEstateItem(data);
      if (created) maintainPropertySitus(created, queryClient);

      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.activityLog(estateId!) });
    },
    onError: (err, variables) => {
      if (isDemoMode) return;
      reportMutationFailure(err, "add", variables.estate_id);
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<CreateEstateItemRequest>;
    }) => {
      if (isDemoMode) return; // onMutate already updated the cache
      return updateEstateItem({ id, estateId: estateId!, updates });
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.estate.items(estateId!) });
      const prev = queryClient.getQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!));
      if (prev) {
        const apply = (items: EstateItem[]) =>
          items.map((item) => (item.id === id ? { ...item, ...updates } : item));
        queryClient.setQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!), {
          ...prev,
          assets: apply(prev.assets),
          debts: apply(prev.debts),
        });
      }
      return { prev };
    },
    onError: (err, _vars, context) => {
      if (isDemoMode) return;
      if (context?.prev) {
        queryClient.setQueryData(queryKeys.estate.items(estateId!), context.prev);
      }
      reportMutationFailure(err, "update", estateId);
    },
    onSuccess: (data) => {
      if (isDemoMode) return;
      const updated = asEstateItem(data);
      if (updated) maintainPropertySitus(updated, queryClient);
    },
    onSettled: () => {
      if (isDemoMode) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.activityLog(estateId!) });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      if (isDemoMode) return;
      return deleteEstateItem({ id, estateId: estateId! });
    },
    onSuccess: (_, id) => {
      if (isDemoMode) {
        const prev = queryClient.getQueryData<EstateItemsResponse>(
          queryKeys.estate.items(estateId!),
        );
        if (prev) {
          queryClient.setQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!), {
            ...prev,
            assets: prev.assets.filter((item) => item.id !== id),
            debts: prev.debts.filter((item) => item.id !== id),
          });
        }
        return;
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.activityLog(estateId!) });
    },
    onError: (err) => {
      if (isDemoMode) return;
      reportMutationFailure(err, "delete", estateId);
    },
  });

  const patchItemStatus = (
    prev: EstateItemsResponse,
    id: number,
    status: "approved" | "declined",
  ) => {
    const patch = (items: EstateItem[]) =>
      items.map((item) => (item.id === id ? { ...item, extraction_status: status } : item));
    return { ...prev, assets: patch(prev.assets), debts: patch(prev.debts) };
  };

  const approveItemMutation = useMutation({
    mutationFn: async (id: number) => {
      if (isDemoMode) return;
      return approveEstateItem({ id, estateId: estateId! });
    },
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.estate.items(estateId!) });
      const prev = queryClient.getQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!));
      if (prev) {
        queryClient.setQueryData<EstateItemsResponse>(
          queryKeys.estate.items(estateId!),
          patchItemStatus(prev, id, "approved"),
        );
      }
      return { prev };
    },
    onSuccess: () => {
      if (isDemoMode) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.activityLog(estateId!) });
    },
    onError: (err, _id, context) => {
      if (context?.prev) {
        queryClient.setQueryData(queryKeys.estate.items(estateId!), context.prev);
      }
      if (isDemoMode) return;
      reportMutationFailure(err, "approve", estateId);
    },
  });

  const declineItemMutation = useMutation({
    mutationFn: async (id: number) => {
      if (isDemoMode) return;
      return declineEstateItem({ id, estateId: estateId! });
    },
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.estate.items(estateId!) });
      const prev = queryClient.getQueryData<EstateItemsResponse>(queryKeys.estate.items(estateId!));
      if (prev) {
        queryClient.setQueryData<EstateItemsResponse>(
          queryKeys.estate.items(estateId!),
          patchItemStatus(prev, id, "declined"),
        );
      }
      return { prev };
    },
    onSuccess: () => {
      if (isDemoMode) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.items(estateId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.estate.activityLog(estateId!) });
    },
    onError: (err, _id, context) => {
      if (context?.prev) {
        queryClient.setQueryData(queryKeys.estate.items(estateId!), context.prev);
      }
      if (isDemoMode) return;
      reportMutationFailure(err, "decline", estateId);
    },
  });

  const assets = useMemo(() => data?.assets ?? [], [data?.assets]);
  const debts = useMemo(() => data?.debts ?? [], [data?.debts]);

  return {
    assets,
    debts,
    summary: data?.summary,
    hasItems: data?.summary?.hasItems || false,
    categories: data?.summary?.categories || [],
    isLoading,
    isFetching,
    error,
    addItem: addItemMutation.mutateAsync,
    isAddingItem: addItemMutation.isPending,
    updateItem: (id: number, updates: Partial<CreateEstateItemRequest>) =>
      updateItemMutation.mutateAsync({ id, updates }),
    isUpdatingItem: updateItemMutation.isPending,
    deleteItem: deleteItemMutation.mutateAsync,
    isDeletingItem: deleteItemMutation.isPending,
    approveItem: approveItemMutation.mutateAsync,
    isApprovingItem: approveItemMutation.isPending,
    declineItem: declineItemMutation.mutateAsync,
    isDecliningItem: declineItemMutation.isPending,
  };
}
